// Shared helpers for blob downloads and file imports.
//
// Why a shared module: every page that downloaded an Excel/PDF was repeating
// the same Blob → URL → <a> dance, AND silently swallowing errors. When the
// backend returns a 4xx/5xx, the response body is itself a Blob (because we
// asked axios for `responseType: 'blob'`), so `err.response?.data?.error`
// returns a Blob object — not a string. The catch then renders 'Export
// failed' with no detail and the operator has no idea what's wrong.
//
// `parseBlobError` reads the Blob as JSON and surfaces the real backend
// message (e.g. "Missing capability: export-excel"). `downloadBlob` saves the
// blob with a filename and cleans up the temporary URL.

import api from '../api/axios';

/**
 * Pull the real error message out of an axios error whose `responseType`
 * is 'blob'. Falls back to the generic axios message if nothing useful
 * can be parsed.
 */
export async function parseBlobError(err) {
  const fallback = err.message || 'Request failed';
  const data = err.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  // axios returns a Blob for blob-typed requests, even on errors.
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      if (!text) return fallback;
      try {
        const json = JSON.parse(text);
        return json.error || json.message || text;
      } catch {
        return text.slice(0, 240);
      }
    } catch {
      return fallback;
    }
  }
  if (typeof data === 'object') {
    return data.error || data.message || fallback;
  }
  return fallback;
}

/**
 * Save a Blob (already obtained from a blob-response) under the given
 * filename and trigger the browser's download flow. Cleans up the
 * object URL after the click so we don't leak memory across exports.
 */
export function downloadBlob(blob, filename, mime) {
  const url = window.URL.createObjectURL(mime ? new Blob([blob], { type: mime }) : new Blob([blob]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // The download starts asynchronously — give the browser a moment before
  // we revoke the URL or large files may abort mid-stream.
  setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
}

/**
 * One-shot download helper: GET a URL with blob response, surface backend
 * errors verbatim. Returns true on success, throws on failure with an
 * Error whose message is the parsed backend error.
 *
 *   await downloadFromApi('/assets/export/excel', 'assets.xlsx');
 */
export async function downloadFromApi(path, filename, mime) {
  try {
    const res = await api.get(path, { responseType: 'blob' });
    downloadBlob(res.data, filename, mime);
    return true;
  } catch (err) {
    const msg = await parseBlobError(err);
    throw new Error(msg);
  }
}
